import { MusicFileUploadIcon, MusicUploadSelectIcon } from '@/components/Icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { HummingAudioType } from '@/service/generatorUpload';
import useGeneratorStore, { R3ImageTypeEnum, R3imagineStatusType } from '@/state/generator-store';
import { cn } from '@/utils';
import dayjs from 'dayjs';
import React from 'react';
import { useModel } from '../model';
import { Trash2Icon } from 'lucide-react';
import { DeleteUploadFileModal } from './DeleteUploadFileModal';
import { useModalManager } from '@/hooks/useModalManager';
import Divider from '@/components/Divider';
import useFlags from '@jenmusic/feature-flag';
import Show from '@/components/Show';

interface DeleteModalData {
  id: number;
  name: string;
}

interface FileSelectItemProps {
  file: HummingAudioType;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: (e: React.MouseEvent) => void;
}

const FileSelectTrigger = ({ selectedFileName }: { selectedFileName?: string }) => (
  <div className="flex justify-between cursor-pointer mt-2 hover:bg-gray-800 p-2 rounded transition duration-200">
    <span className="flex items-center">
      <MusicFileUploadIcon className="w-6 h-6 mr-2" />
      <span className="text-white">{selectedFileName ?? 'Previous uploads'}</span>
    </span>
    <span className="flex items-center">
      <MusicUploadSelectIcon className="w-5 h-5 ml-2" />
    </span>
  </div>
);

const FileSelectItem: React.FC<FileSelectItemProps> = ({ file, isSelected, onSelect, onDelete }) => (
  <div
    className={cn('flex items-center justify-between hover:bg-gray-800 p-2 rounded cursor-pointer relative group transition duration-200', {
      ['bg-gray-800']: isSelected,
      ['pointer-events-none']: file.status !== 'success',
    })}
    onClick={onSelect}
  >
    <div className="flex justify-between items-center w-full">
      <span className="inline-block overflow-hidden text-ellipsis whitespace-nowrap max-w-[240px] text-white text-sm">{file.name}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm text-white/50 whitespace-nowrap">{dayjs(file.created_at).format('HH:mm DD/MM/YY')}</span>
        <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-700 rounded transition duration-200">
          <Trash2Icon className="w-4 h-4 text-red-500" />
        </button>
      </div>
    </div>
  </div>
);

const FileSelect: React.FC = () => {
  const { hummingAudioData, getHummingAudioRun, isOpenHummingAudioList, setIsOpenHummingAudioList } = useModel();

  const { setHummingAudioSelected, hummingAudioSelected, setR3ImageStatus, setR3ImageType } = useGeneratorStore();

  const { isOpen, modalData, openModal, closeModal } = useModalManager<DeleteModalData>();

  const handleDelete = (e: React.MouseEvent, audio: HummingAudioType) => {
    e.stopPropagation();
    openModal({ id: audio.id, name: audio.name });
  };

  const handleSelect = (file: HummingAudioType) => {
    setHummingAudioSelected(file);
    setIsOpenHummingAudioList(false);
    setR3ImageType(R3ImageTypeEnum.select);
    setR3ImageStatus(R3imagineStatusType.WaitingAgree);
  };

  const handleDeleteSuccess = async () => {
    await getHummingAudioRun();
    closeModal();
  };

  const { showNewGenerator } = useFlags();

  return (
    <div className="p-2">
      <Show when={!showNewGenerator}>
        <Divider dashed={true} />
      </Show>
      <Popover open={isOpenHummingAudioList} onOpenChange={setIsOpenHummingAudioList}>
        <PopoverTrigger asChild>
          <div onClick={() => setIsOpenHummingAudioList(true)}>
            <FileSelectTrigger selectedFileName={hummingAudioSelected?.name} />
          </div>
        </PopoverTrigger>
        <PopoverContent align="end" side="top" className="bg-black border-0 p-2 w-[19.5rem] lg:w-[26rem] z-[60]">
          <ScrollArea className="h-[16rem]">
            {!(hummingAudioData?.data ?? []).length ? (
              <div className="text-white/50 text-center min-h-6">No Uploaded Files</div>
            ) : (
              hummingAudioData?.data.map((file) => (
                <FileSelectItem
                  key={file.id}
                  file={file}
                  isSelected={hummingAudioSelected?.id === file.id}
                  onSelect={() => handleSelect(file)}
                  onDelete={(e) => handleDelete(e, file)}
                />
              ))
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {modalData && (
        <DeleteUploadFileModal
          id={modalData.id}
          fileName={modalData.name}
          open={isOpen}
          onOpenChange={closeModal}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
};

export default FileSelect;
